import { ContextMenu, ContextMenuItem, EmptyState } from '@bp/ui-components';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardMenuContainer } from '../../../hooks/useBoardMenuContainer';
import { useFullscreen } from '../../../hooks/useFullscreen';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { GridWeek } from '../GridWeek/GridWeek';
import styles from './GridRows.module.scss';

export const GridRows = observer(() => {
  const { t } = useTranslation();
  const isFullscreen = useFullscreen();
  const menuContainer = useBoardMenuContainer('teaching-block-grid');

  const store = useTeachingBlockStore();

  const contextMenu: ContextMenuItem[] = [
    {
      node: (
        <div className={styles['menu-name']}>{store.selectedRows.map((selected) => selected.label).join(', ')}</div>
      ),
      type: 'component',
    },
    {
      type: 'ruler',
    },
    {
      label: t('pinboard.actions.lockAll'),
      onClick: (e) => {
        e.stopPropagation();
        store.lockCardsInSelectedRows();
      },
    },
    {
      label: t('pinboard.actions.unlockAll'),
      onClick: (e) => {
        e.stopPropagation();
        store.unlockCardsInSelectedRows();
      },
    },
    {
      type: 'ruler',
    },
    {
      label: store.selectedRows.every((r) => store.hiddenRows.has(r.value))
        ? t('pinboard.actions.showRows')
        : t('pinboard.actions.hideRows'),
      onClick: (e) => {
        e.stopPropagation();
        store.toggleRowsVisibility(store.selectedRows);
      },
    },
    {
      label: t('pinboard.actions.removeAll'),
      type: 'error',
      onClick: (e) => {
        e.stopPropagation();
        store.discardCardsInSelectedRows();
      },
    },
  ];

  const today = dayjs().utc().startOf('day');
  const isInCurrentYear = today.isSameOrAfter([...store.dates.keys()][0], 'day');
  const showPast = isInCurrentYear && !!store.currentVersion.active;

  const classes = classNames(styles['grid-rows'], {
    [styles['is-fullscreen']]: isFullscreen,
  });

  // Vergangenheits-Markierung rein über die bekannte Tages-/Wochenstruktur (keine
  // Pixel-Messung via Ref): zählen, wie viele Tage insgesamt in der Vergangenheit
  // liegen, und daraus einen Wochen-Bruchteil bilden. Da alle Wochen gleich breit
  // sind (flex), entspricht der Anteil am gesamten Wochenbereich exakt
  // pastWeeks / totalWeeks.
  const weekEntries = Array.from(store.weeks.entries());
  const totalWeeks = weekEntries.length;
  const pastWeeks = weekEntries.reduce(
    (sum, [, weekDays]) =>
      sum + (showPast ? weekDays.filter((day) => dayjs(day.dateString).isSameOrBefore(today, 'day')).length / 7 : 0),
    0,
  );
  const pastFraction = totalWeeks > 0 ? pastWeeks / totalWeeks : 0;

  const groupByBuilding = store.groupRoomsByBuilding && store.gridContext === 'rooms';

  const rowsByBuilding = new Map<string, typeof store.rows>();
  store.rows?.forEach((row) => {
    const building = row.subLabel?.trim() ?? '';
    rowsByBuilding.set(building, [...(rowsByBuilding.get(building) ?? []), row]);
  });

  return (
    <div className={classes}>
      {store.rows?.length === 0 ? (
        <EmptyState
          title={t('pinboard.empty', { type: t('classes.title', { count: 2 }) })}
          padding='l'
          className={styles.empty}
        />
      ) : (
        <>
          {store.rows?.map((row, rowIndex) => {
            // Beim Gruppieren nach Gebäuden bekommt jede Gruppe eine eigene Überschriftszeile.
            // Rein visuell — bewusst NICHT in `getRows()`, damit Karten-/Auswahl-Logik unberührt bleibt.
            const building = row.subLabel?.trim() ?? '';
            const buildingGroup = rowsByBuilding.get(building) ?? [];
            // Ist das ganze Gebäude ausgeblendet, bleibt nur seine Überschrift stehen —
            // ein Klick darauf blendet die Gruppe wieder ein.
            const collapsed = groupByBuilding && buildingGroup.every((r) => store.hiddenRows.has(r.value));
            const buildingRow =
              groupByBuilding && building !== (store.rows?.[rowIndex - 1]?.subLabel?.trim() ?? '') ? (
                <div
                  className={classNames(styles['building-row'], { [styles.collapsed]: collapsed })}
                  title={collapsed ? t('pinboard.actions.showRows') : t('pinboard.actions.hideRows')}
                  onClick={() => store.toggleRowsVisibility(buildingGroup)}
                >
                  {building || t('common.noBuilding')}
                </div>
              ) : null;

            if (collapsed) {
              return <Fragment key={row.value}>{buildingRow}</Fragment>;
            }

            if (store.hiddenRows.has(row.value)) {
              return (
                <Fragment key={row.value}>
                  {buildingRow}
                  <div
                    data-uuid={row.value}
                    className={styles['hidden-row']}
                    title={row.label}
                    onClick={() => store.toggleRowsVisibility([row])}
                  >
                    <span className={styles['hidden-row-label']}>{row.label}</span>
                  </div>
                </Fragment>
              );
            }
            return (
              <Fragment key={row.value}>
                {buildingRow}
                <div className={styles.row} style={{ height: store.rowHeight }} data-uuid={row.value}>
                  {pastFraction > 0 && (
                    <div
                      title={t('pinboard.pastTimeframe')}
                      className={styles['today-indicator']}
                      style={{ width: `calc((100% - var(--pinboard-name-column-size)) * ${pastFraction})` }}
                    ></div>
                  )}
                  <ContextMenu data={contextMenu} parent={menuContainer}>
                    <div
                      className={styles['name-wrapper']}
                      onClick={(event) => {
                        store.rowClickHandler(event, { label: row.label, value: row.value });
                      }}
                      title={row.subLabel ? `${row.label} (${row.subLabel})` : row.label}
                      onContextMenu={() => {
                        if (store.selectedRows.length <= 1 || !store.selectedRows.includes(row))
                          store.selectRows([row]);
                      }}
                    >
                      <div
                        className={classNames(styles.name, {
                          [styles.selected]: store.selectedRows.find((r) => r.value === row.value),
                        })}
                      >
                        {row.label}
                      </div>
                    </div>
                  </ContextMenu>

                  {weekEntries.map(([weekNumber, weekDays], index) => {
                    const firstDay = weekDays[0];
                    return (
                      <GridWeek
                        key={weekNumber + '_' + index}
                        row={row}
                        weekNumber={firstDay.weekNumber}
                        year={dayjs(firstDay.dateString).isoWeekYear()}
                        days={weekDays}
                      />
                    );
                  })}
                </div>
              </Fragment>
            );
          })}
        </>
      )}
    </div>
  );
});
